(function () {
  const ANNOUNCEMENT_BAR_DISMISS_COOKIE = 'announcement_bar_dismissed';

  /**
   * Minimum height per bar when summing (reduces CLS).
   * Mobile uses a slightly larger floor to better match compact layouts.
   */
  const ANNOUNCEMENT_BAR_HEIGHT_BASE_PX_DESKTOP = 29;
  const ANNOUNCEMENT_BAR_HEIGHT_BASE_PX_MOBILE = 36;

  function getAnnouncementBarHeightBasePx() {
    return window.matchMedia('(max-width: 767px)').matches
      ? ANNOUNCEMENT_BAR_HEIGHT_BASE_PX_MOBILE
      : ANNOUNCEMENT_BAR_HEIGHT_BASE_PX_DESKTOP;
  }

  /**
   * Cookie value is a comma-separated list of content hashes (one per dismissed bar).
   * Legacy single-hash values still parse as a one-item list.
   */
  function parseAnnouncementBarDismissList(cookieValue) {
    if (!cookieValue) return [];
    return cookieValue
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }

  function persistAnnouncementBarDismissHash(hash, expiryDays) {
    if (!hash || typeof window.setCookie !== 'function') return;
    const set = new Set(
      parseAnnouncementBarDismissList(window.getCookie(ANNOUNCEMENT_BAR_DISMISS_COOKIE)),
    );
    set.add(hash);
    window.setCookie(ANNOUNCEMENT_BAR_DISMISS_COOKIE, Array.from(set).join(','), expiryDays);
  }

  function isAnnouncementBarHashDismissedInCookie(hash) {
    if (!hash || typeof window.getCookie !== 'function') return false;
    return parseAnnouncementBarDismissList(window.getCookie(ANNOUNCEMENT_BAR_DISMISS_COOKIE)).includes(
      hash,
    );
  }

  window.parseAnnouncementBarDismissList = parseAnnouncementBarDismissList;

  /**
   * Sums each visible bar’s measured height with a 29px floor per bar (helps CLS when multiple bars stack).
   */
  function syncAnnouncementBarHeightFromDom() {
    const isRendered = (el) => {
      if (!el || !el.isConnected) return false;
      const styles = window.getComputedStyle(el);
      if (styles.display === 'none' || styles.visibility === 'hidden') return false;
      return el.getClientRects().length > 0;
    };

    const basePx = getAnnouncementBarHeightBasePx();
    let totalPx = 0;
    document.querySelectorAll('announcement-bar').forEach((bar) => {
      if (bar.classList.contains('announcement-bar--dismissed')) return;
      if (bar.style.display === 'none') return;
      if (!isRendered(bar)) return;
      totalPx += Math.max(basePx, bar.offsetHeight);
    });
    document.documentElement.style.setProperty('--announcement-bar-height', `${totalPx}px`);
  }

  window.syncAnnouncementBarHeightFromDom = syncAnnouncementBarHeightFromDom;

  let announcementBarHeightRaf = null;
  function scheduleAnnouncementBarHeightSync() {
    if (announcementBarHeightRaf !== null) {
      cancelAnimationFrame(announcementBarHeightRaf);
    }
    announcementBarHeightRaf = requestAnimationFrame(() => {
      announcementBarHeightRaf = null;
      syncAnnouncementBarHeightFromDom();
    });
  }

  window.addEventListener('resize', scheduleAnnouncementBarHeightSync, { passive: true });
  window.addEventListener('orientationchange', scheduleAnnouncementBarHeightSync, { passive: true });
  window.addEventListener('pageshow', scheduleAnnouncementBarHeightSync, { passive: true });

  if (!window.customElements.get('announcement-bar')) {
    class AnnouncementBar extends HTMLElement {
      connectedCallback() {
        if (this.classList.contains('announcement-bar--dismissed')) {
          return;
        }

        this._abortController = new AbortController();
        const { signal } = this._abortController;

        this._resizeObserver = new ResizeObserver(() => {
          syncAnnouncementBarHeightFromDom();
        });
        this._resizeObserver.observe(this);

        this.setupDismissButton(signal);
        this.setupAccessibility(signal);

        requestAnimationFrame(() => {
          syncAnnouncementBarHeightFromDom();
        });
      }

      disconnectedCallback() {
        this._abortController?.abort();
        this._resizeObserver?.disconnect();
        requestAnimationFrame(() => {
          syncAnnouncementBarHeightFromDom();
        });
      }

      get enableDismiss() {
        const raw = this.getAttribute('data-enable-dismiss');
        if (raw === null) return true;
        const s = String(raw).trim().toLowerCase();
        if (s === '' || s === 'false' || s === '0' || s === 'no') return false;
        return true;
      }

      get contentHash() {
        return this.dataset.contentHash || '';
      }

      get dismissExpiryDays() {
        const days = parseInt(this.dataset.dismissExpiryDays, 10);
        return Number.isFinite(days) ? days : 3;
      }

      updateHeaderHeight() {
        const header = document.querySelector('header');
        if (!header) return;

        const headerHeight = header.offsetHeight;
        document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
        document.documentElement.style.setProperty('--mobile-header-height', `${headerHeight}px`);
      }

      setupDismissButton(signal) {
        if (!this.enableDismiss) return;

        // Delegate: connectedCallback can run before children are parsed, so direct
        // querySelector on the button often misses; bubbled clicks still reach the host.
        this.addEventListener(
          'click',
          (e) => {
            const close = e.target.closest('[data-dismiss-announcement]');
            if (!close || !this.contains(close)) return;
            e.preventDefault();
            this.dismiss();
          },
          { signal },
        );
      }

      setupAccessibility(signal) {
        if (!this.enableDismiss) return;

        this.addEventListener(
          'keydown',
          (e) => {
            if (e.key === 'Escape' || e.key === 'Esc') {
              this.dismiss();
            }
          },
          { signal },
        );
      }

      dismiss() {
        if (this.isAnimatingDismiss) return;

        this.isAnimatingDismiss = true;
        this.saveDismissState();
        this.hide(true);
      }

      hide(animate = true) {
        const inner = this.firstElementChild;
        if (!inner) {
          this.isAnimatingDismiss = false;
          return;
        }
        this.finalizeHide();
      }

      finalizeHide() {
        this.style.display = 'none';
        this.classList.add('announcement-bar--dismissed');
        syncAnnouncementBarHeightFromDom();
        this.updateHeaderHeight();

        document.dispatchEvent(new CustomEvent('announcement-bar:dismissed'));
      }

      isDismissed() {
        return isAnnouncementBarHashDismissedInCookie(this.contentHash);
      }

      saveDismissState() {
        persistAnnouncementBarDismissHash(this.contentHash, this.dismissExpiryDays);
      }
    }

    window.customElements.define('announcement-bar', AnnouncementBar);
  }
})();
